import React, { useEffect, useRef, useState } from 'react'
import { useJobFlowTracking } from '../../hooks/useJobFlowTracking';
import { LuFileText, LuPhone, LuUserPlus, LuUserCheck, LuMonitorPlay, LuCheck, LuGripVertical, LuArrowUp, LuArrowDown, LuX, LuArrowRight } from 'react-icons/lu';
import { FaArrowLeft } from "react-icons/fa";
import { getHiringStages, setupAutomations, generatePreScreeningQuestions, fetchJobById } from '../../Redux/ActionCreators/jobsAction';
import { updateHiringAssistaniData } from '../../Redux/ActionCreators/qualifyQuestionsAction';
import { getSubscriptionPlans } from '../../Redux/ActionCreators/userAction';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PreScreening from '../JobScreening/preScreening';
import TeleScreening from '../JobScreening/teleScreening';
import { Snackbar, Alert } from '@mui/material';

const FEATURE_MAPPING = {
    "Resume Scoring": {
        description: 'Rank candidates by how closely their resume aligns with the job description.',
        icon: <LuFileText size={24} />,
    },
    "Pre Screening": {
        description: 'Qualifying questions that filter candidates before the first interview.',
        icon: <LuUserPlus size={24} />,
    },
    "AI Telephonic Screening": {
        description: 'First-round voice screening with a job-fit analysis for every call.',
        icon: <LuPhone size={24} />,
    },
    "AI Candidate Assessments": {
        description: 'Skill-based technical and behavioral tests tailored to the role.',
        icon: <LuUserCheck size={24} />,
    },
    "AI Video Interviews": {
        description: 'A 30-minute structured video interview with flexible scheduling.',
        icon: <LuMonitorPlay size={24} />,
    },
};

const ORDERED_NAMES = [
    { name: "Resume Scoring", id: "cf1bb4ea-355b-4861-ad7b-ba52d3ce6b82" },
    { name: "Pre Screening", id: "3dab71cf-c680-4983-a109-1cff0953999b" },
    { name: "AI Telephonic Screening", id: "fd40e3d2-38a9-4cef-937a-93ef6a42d045" },
    { name: "AI Candidate Assessments", id: "293e9b95-4610-48f2-ae93-c7264cae7d6a" },
    { name: "AI Video Interviews", id: "ed658e02-1549-4d9e-a735-1f1c1e385f46" }
];

const NAME_BY_STAGE_ID = ORDERED_NAMES.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {});

// Resume Scoring always runs first when selected: it can be removed, but never moved.
const RESUME_SCORING_ID = "cf1bb4ea-355b-4861-ad7b-ba52d3ce6b82";

const bySequence = (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0);

const pinResumeScoring = (ids) =>
    ids.includes(RESUME_SCORING_ID)
        ? [RESUME_SCORING_ID, ...ids.filter(id => id !== RESUME_SCORING_ID)]
        : ids;

// Keeps the user's current order for stages that are still enabled, drops the
// ones that were switched off and appends newly enabled ones (by sequence).
const syncOrder = (prevOrder, stages) => {
    const enabledIds = stages
        .filter(s => s.is_enabled && !s.is_locked && NAME_BY_STAGE_ID[s.hiring_stage_id])
        .sort(bySequence)
        .map(s => s.hiring_stage_id);
    const kept = prevOrder.filter(id => enabledIds.includes(id));
    const added = enabledIds.filter(id => !kept.includes(id));
    return pinResumeScoring([...kept, ...added]);
};

function Stage2SelectAIFeatures({ jobId, onNext, onBack, transitioning, transitionMessage, flowSessionId, isRestrictedEdit }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [order, setOrder] = useState([]);
    const [loadingStages, setLoadingStages] = useState(false);
    const [saving, setSaving] = useState(false);

    const [activeSettingsView, setActiveSettingsView] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [lockedModal, setLockedModal] = useState(false);
    const [generatingPreScreening, setGeneratingPreScreening] = useState(false);
    const hasGeneratedPreScreening = useRef(false);

    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    const { trackComplete } = useJobFlowTracking({
        flowSessionId,
        stepId: 'select_ai_features',
        stepLabel: 'Select AI Features',
        jobId,
    });

    const { hiringStages } = useSelector(state => state.jobs);
    const { selectedTenant } = useSelector((state) => state.users);

    const automationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags
      ?.automation_workflows === true || selectedTenant?.data?.subscription_plan?.configuration?.feature_flags
      ?.automation === true;

    useEffect(() => {
        const fetchStages = async () => {
            if (!jobId || transitioning) return;
            const tenantId = localStorage.getItem("tenentId");
            setLoadingStages(true);
            await dispatch(getSubscriptionPlans(tenantId)).catch(() => {});
            await dispatch(getHiringStages(jobId));
            setLoadingStages(false);
        };
        fetchStages();
    }, [dispatch, jobId, transitioning]);

    useEffect(() => {
        if (hiringStages && hiringStages.length > 0) {
            setOrder(prev => syncOrder(prev, hiringStages));
        }
    }, [hiringStages]);

    // After stages load, generate pre-screening questions once (skip for restricted edits of existing jobs — don't overwrite saved questions)
    useEffect(() => {
        if (!jobId || transitioning || isRestrictedEdit || !hiringStages?.length || hasGeneratedPreScreening.current) return;
        hasGeneratedPreScreening.current = true;

        const runGenerate = async () => {
            setGeneratingPreScreening(true);
            const job = await dispatch(fetchJobById(jobId));
            if (job) {
                const body = {
                    job_title: job.title || '',
                    company_name: job.company_name || '',
                    job_type: job.job_type?.name || job.job_type || '',
                    skills_required: Array.isArray(job.skills) ? job.skills.map(s => s.name || s) : [],
                    job_location_iso: job.country?.iso2 || '',
                    job_location_text: [job.city?.name, job.country?.name].filter(Boolean).join(', '),
                    job_location_city: job.city?.name || '',
                    job_location_country: job.country?.name || '',
                    experience_required: job.experience_required || '',
                    job_salary: job.salary || '',
                    job_qualification: job.qualifications || '',
                    job_description: job.description || '',
                };
                await dispatch(generatePreScreeningQuestions(jobId, body));
            }
            setGeneratingPreScreening(false);
        };
        runGenerate();
    }, [jobId, transitioning, hiringStages, dispatch, isRestrictedEdit]);

    const findStage = (hiringStageId) => (hiringStages || []).find(s => s.hiring_stage_id === hiringStageId);

    const toggleFeature = async (stage) => {
        if (stage.is_locked) {
            setLockedModal(true);
            return;
        }
        const nextState = !order.includes(stage.hiring_stage_id);

        // Enforce at least one selection
        if (!nextState && order.length <= 1) {
            setSnackbar({ open: true, message: 'At least one screening option must stay selected', severity: 'warning' });
            return;
        }

        setLoadingStages(true);
        try {
            const resp = await dispatch(updateHiringAssistaniData(jobId, stage.id, { is_enabled: nextState }));
            if (resp && resp.success !== false) {
                await dispatch(getHiringStages(jobId));
            } else {
                setSnackbar({ open: true, message: resp?.message || 'Failed to update feature state' });
            }
        } catch (error) {
            console.error("Failed to update feature:", error);
            setSnackbar({ open: true, message: 'Failed to update feature state' });
        } finally {
            setLoadingStages(false);
        }
    }

    const handleEditClick = (e, orderedItemName) => {
        e.stopPropagation();
        if (orderedItemName === 'Pre Screening') {
            setActiveSettingsView('Pre Screening');
        } else if (orderedItemName === 'AI Telephonic Screening') {
            setActiveSettingsView('Voice Call');
        }
    }

    // ── Ordering ────────────────────────────────────────────────────────────
    // First index a movable stage may occupy (1 while Resume Scoring holds the top)
    const firstMovableIndex = order[0] === RESUME_SCORING_ID ? 1 : 0;

    const moveItem = (fromIndex, toIndex) => {
        if (fromIndex < firstMovableIndex || toIndex < firstMovableIndex) return;
        if (toIndex >= order.length || fromIndex === toIndex) return;
        setOrder(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    };

    const handleDragStart = (e, id) => {
        setDraggingId(id);
        e.dataTransfer.effectAllowed = 'move';
        // Firefox won't start a drag without data set
        e.dataTransfer.setData('text/plain', id);
    };

    const handleDragOver = (e, id) => {
        // Not a drop target: nothing can be placed above Resume Scoring
        if (id === RESUME_SCORING_ID) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (id !== dragOverId) setDragOverId(id);
    };

    const handleDrop = (e, id) => {
        e.preventDefault();
        if (draggingId && draggingId !== id) {
            moveItem(order.indexOf(draggingId), order.indexOf(id));
        }
        setDraggingId(null);
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDragOverId(null);
    };

    // Enabled stages take sequence 1..n in the chosen order; disabled stages
    // follow in their existing relative order so sequences never collide.
    const saveOrder = async () => {
        const stages = hiringStages || [];
        const disabled = stages.filter(s => !order.includes(s.hiring_stage_id)).sort(bySequence);
        const desired = [...order.map(findStage).filter(Boolean), ...disabled];
        const changed = desired
            .map((stage, idx) => ({ stage, sequence: idx + 1 }))
            .filter(({ stage, sequence }) => stage.sequence !== sequence);
        if (changed.length === 0) return true;

        const results = await Promise.all(
            changed.map(({ stage, sequence }) => dispatch(updateHiringAssistaniData(jobId, stage.id, { sequence })))
        );
        return results.every(resp => resp && resp.success !== false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const orderSaved = await saveOrder();
            if (!orderSaved) {
                setSnackbar({ open: true, message: 'Failed to save the screening order' });
                return;
            }
            if (isRestrictedEdit) {
                trackComplete(jobId);
                navigate('/jobs');
                return;
            }
            if (automationEnabled) {
                // Backend expects this job's own hiring-stage ids, not the global stage-type ids.
                // Resume Scoring runs on intake and isn't an automation pipeline stage, so it's left out.
                const stageOrder = order
                    .filter(id => id !== RESUME_SCORING_ID)
                    .map(id => findStage(id)?.id)
                    .filter(Boolean);
                const resp = await dispatch(setupAutomations(jobId, stageOrder));
                if (!resp) {
                    setSnackbar({ open: true, message: 'Failed to set up automation for this job' });
                    return;
                }
            }
            trackComplete(jobId);
            if (onNext) onNext();
        } catch (error) {
            setSnackbar({ open: true, message: 'Something went wrong while saving the job' });
        } finally {
            setSaving(false);
        }
    };

    if (activeSettingsView === 'Pre Screening') {
        return (
            <PreScreening jobId={jobId} setType={() => setActiveSettingsView(null)} />
        )
    }

    if (activeSettingsView === 'Voice Call') {
        return <TeleScreening jobId={jobId} setType={() => setActiveSettingsView(null)} />
    }

    if (transitioning) {
        return (
            <div className="cj-stage2" style={{ pointerEvents: 'none' }}>
                <div className="cj-stage-header">
                    <div>
                        <p className="gradient-text-loading">{transitionMessage}</p>
                    </div>
                </div>
                <div className="cj-features-grid">
                    {[1, 2, 3, 4, 5].map((idx) => (
                        <div key={idx} className="gradient-border-wrap" style={{ marginBottom: '16px' }}>
                            <div className="cj-feature-card-empty-skeleton" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const orderedStagesForRender = ORDERED_NAMES.map(orderedItem => {
        const stage = findStage(orderedItem.id);
        if (!stage) return null;
        return { stage, orderedItem };
    }).filter(Boolean);

    const busy = loadingStages || saving || generatingPreScreening;

    return (
        <div className="cj-stage2 cj-screening">
            {lockedModal && (
                <div className="cj-locked-modal-overlay" onClick={() => setLockedModal(false)}>
                    <div className="cj-locked-modal" onClick={e => e.stopPropagation()}>
                        <button className="cj-locked-modal-close" onClick={() => setLockedModal(false)} aria-label="Close">&#x2715;</button>
                        <div className="cj-locked-modal-icon">&#128274;</div>
                        <h3 className="cj-locked-modal-title">Feature Not Available</h3>
                        <p className="cj-locked-modal-message">This feature is not available on your current plan. Please upgrade your plan to use this feature.</p>
                    </div>
                </div>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity || 'error'}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <div>
                <div className="cj-stage-header">
                    <div>
                        <button className="cj-back-link" onClick={onBack} disabled={busy} style={{ cursor: busy ? 'not-allowed' : '' }}>
                            <FaArrowLeft /> Back to Job Details
                        </button>
                    </div>
                    <div className="cj-mobile-sticky-bar">
                        <button className="cj-btn-primary" onClick={handleSave} disabled={busy || order.length === 0} style={{ cursor: busy ? 'not-allowed' : '' }}>
                            {saving
                                ? 'Saving…'
                                : isRestrictedEdit
                                    ? 'Save'
                                    : (<>Save job &amp; invite <LuArrowRight size={16} /></>)}
                        </button>
                    </div>
                </div>
                <div>
                    <h3 className="cj-stage-title">Select Screening Options</h3>
                    <p className="cj-stage-subtitle">
                        Gappeo has preselected the most used features. Customize them for your requirements.
                    </p>
                </div>
            </div>

            <div className="cj-screening-layout">
                <div className="cj-screening-grid" style={{ pointerEvents: generatingPreScreening || saving ? 'none' : 'auto' }}>
                    {orderedStagesForRender.map(({ stage, orderedItem }) => {
                        const mapData = FEATURE_MAPPING[orderedItem.name];
                        const isSelected = order.includes(stage.hiring_stage_id);
                        const isLocked = stage.is_locked;
                        const isGenerating = orderedItem.name === 'Pre Screening' && generatingPreScreening;
                        const isEditable = ['Pre Screening', 'AI Telephonic Screening'].includes(orderedItem.name);

                        return (
                            <div
                                key={stage.hiring_stage_id}
                                className={`cj-screen-card${isSelected ? ' cj-screen-card--selected' : ''}${isLocked ? ' cj-screen-card--locked' : ''}`}
                                onClick={() => !isGenerating && toggleFeature(stage)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isGenerating) { e.preventDefault(); toggleFeature(stage); } }}
                                aria-pressed={isSelected}
                            >
                                <div className="cj-screen-card-top">
                                    <span className="cj-screen-card-icon">{mapData.icon}</span>
                                    <div className="cj-screen-card-actions">
                                        {isEditable && isSelected && !isGenerating && (
                                            <button
                                                className="cj-screen-card-edit"
                                                onClick={(e) => handleEditClick(e, orderedItem.name)}
                                                title="Configure"
                                                aria-label={`Configure ${orderedItem.name}`}
                                            >
                                                <img src="/images/square-pen-black.svg" alt="" width={16} height={16} />
                                            </button>
                                        )}
                                        <span className={`cj-screen-card-pill${isSelected ? ' cj-screen-card-pill--selected' : ''}`}>
                                            {isLocked ? 'Locked' : isSelected ? (<><LuCheck size={12} /> Selected</>) : 'Select'}
                                        </span>
                                    </div>
                                </div>
                                <p className="cj-screen-card-title">{orderedItem.name}</p>
                                <p className={`cj-screen-card-desc${isGenerating ? ' gradient-text-loading' : ''}`}>
                                    {isGenerating ? 'Creating Pre Screening questions, please wait..' : mapData.description}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <aside className="cj-order-panel">
                    <div className="cj-order-head">
                        <h4>Screening order</h4>
                        <span className="cj-order-count">{order.length}</span>
                    </div>

                    {order.length === 0 ? (
                        <p className="cj-order-empty">Select at least one screening option.</p>
                    ) : (
                        <ul className="cj-order-list">
                            {order.map((id, index) => {
                                const isPinned = id === RESUME_SCORING_ID;
                                return (
                                    <li
                                        key={id}
                                        className={`cj-order-item${isPinned ? ' cj-order-item--pinned' : ''}${draggingId === id ? ' cj-order-item--dragging' : ''}${dragOverId === id && draggingId !== id ? ' cj-order-item--over' : ''}`}
                                        draggable={!busy && !isPinned}
                                        onDragStart={(e) => handleDragStart(e, id)}
                                        onDragOver={(e) => handleDragOver(e, id)}
                                        onDrop={(e) => handleDrop(e, id)}
                                        onDragEnd={handleDragEnd}
                                        title={isPinned ? 'Resume Scoring always runs first' : undefined}
                                    >
                                        <span className="cj-order-grip" aria-hidden="true"><LuGripVertical size={14} /></span>
                                        <span className="cj-order-index">{index + 1}</span>
                                        <span className="cj-order-name">{NAME_BY_STAGE_ID[id]}</span>
                                        <div className="cj-order-controls">
                                            {!isPinned && (
                                                <>
                                                    <button onClick={() => moveItem(index, index - 1)} disabled={busy || index <= firstMovableIndex} aria-label="Move up"><LuArrowUp size={14} /></button>
                                                    <button onClick={() => moveItem(index, index + 1)} disabled={busy || index === order.length - 1} aria-label="Move down"><LuArrowDown size={14} /></button>
                                                </>
                                            )}
                                            <button onClick={() => { const stage = findStage(id); if (stage) toggleFeature(stage); }} disabled={busy || order.length <= 1} aria-label="Remove"><LuX size={14} /></button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <p className="cj-order-hint">Candidates move through steps in this order. Drag or use the arrows.</p>
                </aside>
            </div>
        </div>
    )
}

export default Stage2SelectAIFeatures
